// src/helpers/citationGroups.ts

/**
 * Turns a run of adjacent citation refs into the chips it should render: one
 * chip per citation type (playbook, Catylex, document), in order of first
 * appearance, each listing the refs it stands for.
 *
 * Before splitting, refs with no payload yet are skipped (the context update
 * re-renders), and a repeated ref, or a ref whose payload is identical to an
 * earlier one, counts once (Anthropic gives each citation of the same passage
 * its own ref). A playbook citation whose breadcrumb is a strict prefix of
 * another playbook citation's breadcrumb in the run is dropped: the topic,
 * position and tier headings are already shown in the provision's breadcrumb.
 */

import type { DocumentCitation } from "../interfaces/documentCitationTypes";
import { isCatylexCitation, isPlaybookCitation } from "../interfaces/documentCitationTypes";

export type CitationKind = "playbook" | "catylex" | "document";

export interface CitationGroup {
  kind: CitationKind;
  /** First ref is the chip's own citation; the rest are counted as "+N". */
  refs: string[];
}

export const citationKind = (citation: DocumentCitation): CitationKind => {
  if (isPlaybookCitation(citation)) return "playbook";
  if (isCatylexCitation(citation)) return "catylex";

  return "document";
};

const identity = (citation: DocumentCitation): string =>
  JSON.stringify([
    citationKind(citation),
    citation.document_title,
    "breadcrumb" in citation ? citation.breadcrumb : null,
    citation.cited_text,
  ]);

const isStrictPrefix = (shorter: string[], longer: string[]): boolean =>
  shorter.length < longer.length && shorter.every((level, i) => level === longer[i]);

export const groupCitations = (
  refs: string[],
  lookupCitation: (ref: string) => DocumentCitation | undefined
): CitationGroup[] => {
  const seenRefs = new Set<string>();
  const seenPayloads = new Set<string>();
  const resolved: { ref: string; citation: DocumentCitation }[] = [];

  for (const ref of refs) {
    if (seenRefs.has(ref)) continue;
    seenRefs.add(ref);
    const citation = lookupCitation(ref);

    if (!citation) continue;
    const key = identity(citation);

    if (seenPayloads.has(key)) continue;
    seenPayloads.add(key);
    resolved.push({ ref, citation });
  }

  const kept = resolved.filter(({ citation }) => {
    if (!isPlaybookCitation(citation)) return true;

    return !resolved.some(
      ({ citation: other }) =>
        other !== citation &&
        isPlaybookCitation(other) &&
        other.document_title === citation.document_title &&
        isStrictPrefix(citation.breadcrumb, other.breadcrumb)
    );
  });

  const groups: CitationGroup[] = [];

  for (const { ref, citation } of kept) {
    const kind = citationKind(citation);
    const group = groups.find((g) => g.kind === kind);

    if (group) {
      group.refs.push(ref);
    } else {
      groups.push({ kind, refs: [ref] });
    }
  }

  return groups;
};
